import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    Easing,
    Platform,
    Pressable,
    StyleSheet,
    TextInput,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { Chapter } from "./Chapter";
import { FullBleedBand } from "./FullBleedBand";
import { SourceChip } from "./SourceChip";

import { useIcdSearch } from "@/hooks/useIcdSearch";
import type { ICDSuggestion, PatientDiagnosisItem } from "@/lib/api";
import type { AnswerBasedSuggestion } from "@/lib/diagnosis-map";
import { colors, fonts, gradients, iconTints } from "@/lib/theme";

// Confidence → badge palette (tokens only, per the design-system rule).
const CONFIDENCE_TINT = {
    High: iconTints.tealStrong,
    Medium: iconTints.amber,
    Low: iconTints.neutral
} as const;

export type DiagnosesPhase = "idle" | "analyzing" | "results" | "errored";

/**
 * Four kinds — copied 1:1 from ICDStep so the persistence + display semantics
 * are identical:
 *
 *   ai-on      AI suggested · RN confirmed         (will save)
 *   ai-off     AI suggested · RN un-confirmed      (won't save)
 *   ai-locked  Already on patient profile          (read-only, won't save)
 *   rn         RN-added manually                    (will save)
 *
 * Save filter: any item where `confirmed === true && alreadyOnProfile === false`
 * gets saved through saveDiagnoses with its `suggestedByAi` flag preserved.
 * The orchestrator's persistDiagnoses uses this rule.
 */
export interface DiagnosisRow {
    code: string;
    description: string | null;
    confirmed: boolean;
    suggestedByAi: boolean;
    alreadyOnProfile: boolean;
    /**
     * Server-side `visit_diagnoses.id` for rows hydrated from the patient
     * diagnoses endpoint (i.e. already persisted to the chart). Used by
     * the orchestrator to fire DELETE when an RN un-selects a previously-
     * attested Zone 2 row. `null` for fresh AI suggestions and RN-typed
     * codes that haven't been saved yet (those rely on saveDiagnoses to
     * insert + the next focus-refetch to re-hydrate with an id).
     */
    id?: number | null;
}

interface Props {
    phase: DiagnosesPhase;
    diagnoses: DiagnosisRow[];
    /** Codes whose write (select/add/delete) is in flight — disables that row's
     * pill and shows a spinner so the pessimistic write reads as responsive. */
    pendingCodes?: Set<string>;
    /** Updates a single row's confirmed flag (AI rows only — profile is locked). */
    onToggleConfirm: (code: string) => void;
    /** Adds an RN-typed code through the inline search panel. */
    onAddManually: (code: string, description: string) => void;
    /** Removes an RN-added row. */
    onRemoveRn: (code: string) => void;
    /** Kicks off the AI scan — orchestrator calls fetchIcdSuggestions. */
    onRunScan: () => void;
    /** Section name surfaces in the source chips (e.g. "Cardiopulmonary"). */
    sectionName: string;
    readOnly: boolean;
    /** Bumped by the orchestrator when scrolling here from a validation error card. */
    addManuallyHighlightTick: number;
    /**
     * Deterministic answer-based suggestions for this section (symptom→diagnosis
     * matrix). Rendered as their own group above the AI band; already-selected /
     * profile codes are filtered out upstream. Empty/undefined → group hidden.
     */
    answerBased?: AnswerBasedSuggestion[];
    /** Persists an answer-based pick (source='matrix') via the normal save path. */
    onSelectAnswerBased?: (suggestion: AnswerBasedSuggestion) => void;
}

/**
 * Chapter 03 — flat-section diagnoses surface.
 *
 * Lifecycle:
 *   - idle:      shown when there are no AI suggestions yet AND no codes are
 *                already confirmed on the patient profile for this section.
 *                Renders the Suggest Diagnosis CTA + source-chip preview.
 *   - analyzing: visible during the LLM scan (~10s).
 *   - results:   the four-kind list. Reached on scan complete OR on mount when
 *                there are pre-existing rows (cached AI suggestions, profile
 *                codes, or previously-confirmed-this-visit codes).
 *
 * The "Add another diagnosis" trigger inside results expands into an INLINE
 * pink-bordered search panel — no modal, no sheet. searchLocalICD10 powers
 * the lookup. Tap a result → onAddManually fires, keyboard stays open for
 * the next add.
 */
export function DiagnosesChapter({
    phase,
    diagnoses,
    pendingCodes,
    onToggleConfirm,
    onAddManually,
    onRemoveRn,
    onRunScan,
    sectionName,
    readOnly,
    addManuallyHighlightTick,
    answerBased,
    onSelectAnswerBased
}: Props) {
    const [glow, setGlow] = useState(false);
    useEffect(() => {
        if (addManuallyHighlightTick > 0) {
            setGlow(true);
            const t = setTimeout(() => setGlow(false), 1800);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [addManuallyHighlightTick]);

    // 3-zone split per the canonical design:
    //   Z1 (inside band)    = pending — AI suggested + not yet selected
    //   Z2 (below band)     = selected — anything saved to this section
    //   Z3 (collapsed strip)= other   — patient profile + cross-section codes
    const { pending, selected, other } = useMemo(() => splitIntoZones(diagnoses), [diagnoses]);
    const existingCodes = useMemo(() => new Set(diagnoses.map((d) => d.code)), [diagnoses]);

    // FullBleedBand's legacy `phase` prop only knows idle/analyzing/results.
    // For the new "errored" phase, switch to the `tone="err"` channel so the
    // band wash turns warning-tinted under the DxError component.
    const bandPhase: "idle" | "analyzing" | "results" | undefined =
        phase === "errored" ? undefined : phase;
    const bandTone = phase === "errored" ? ("err" as const) : undefined;

    return (
        <Chapter num="03" title="Diagnoses" helper="Confirm what fits." padBottom={28}>
            {answerBased && answerBased.length > 0 ? (
                <AnswerBasedGroup
                    suggestions={answerBased}
                    pendingCodes={pendingCodes}
                    readOnly={readOnly}
                    onSelect={onSelectAnswerBased}
                />
            ) : null}

            <FullBleedBand phase={bandPhase} tone={bandTone}>
                {phase === "idle" ? <DxIdle onRun={onRunScan} readOnly={readOnly} /> : null}
                {phase === "analyzing" ? <DxAnalyzing /> : null}
                {phase === "errored" ? <DxError onRetry={onRunScan} readOnly={readOnly} /> : null}
                {phase === "results" ? (
                    <DxResults
                        pending={pending}
                        pendingCodes={pendingCodes}
                        sectionName={sectionName}
                        onSelect={onToggleConfirm}
                        readOnly={readOnly}
                    />
                ) : null}
                {phase !== "analyzing" ? (
                    <AddMorePanel
                        existingCodes={existingCodes}
                        onAdd={onAddManually}
                        readOnly={readOnly}
                        glow={glow}
                    />
                ) : null}
            </FullBleedBand>

            <Zone2List
                selected={selected}
                pendingCodes={pendingCodes}
                sectionName={sectionName}
                readOnly={readOnly}
                onUnselectAi={onToggleConfirm}
                onRemoveRn={onRemoveRn}
            />

            <OtherStripe rows={other} sectionName={sectionName} readOnly={readOnly} />
        </Chapter>
    );
}

// ── Answer-based suggestions (symptom→diagnosis matrix) ────────────────────

/**
 * Deterministic suggestions derived on-device from the symptoms the RN selected
 * (no LLM). Rendered as its own group above the AI band. Selecting a row saves
 * it through the normal diagnoses path tagged source='matrix'; the next refetch
 * then surfaces it in Zone 2 and the upstream filter drops it from here.
 */
function AnswerBasedGroup({
    suggestions,
    pendingCodes,
    readOnly,
    onSelect
}: {
    suggestions: AnswerBasedSuggestion[];
    pendingCodes?: Set<string>;
    readOnly: boolean;
    onSelect?: (suggestion: AnswerBasedSuggestion) => void;
}) {
    return (
        <View style={dxStyles.abGroup}>
            <View style={dxStyles.resultsChipRow}>
                <View style={dxStyles.resultsChip}>
                    <MaterialCommunityIcons
                        name="clipboard-check-outline"
                        size={11}
                        color={colors.teal600}
                    />
                    <Text style={dxStyles.resultsChipText}>BASED ON YOUR ANSWERS</Text>
                </View>
            </View>
            <Text style={dxStyles.abHelper}>
                Suggested from the symptoms you selected. Tap to add any that apply.
            </Text>
            <View style={{ gap: 8 }}>
                {suggestions.map((sug) => (
                    <AnswerBasedCard
                        key={sug.icdCode}
                        suggestion={sug}
                        readOnly={readOnly}
                        pending={pendingCodes?.has(sug.icdCode) ?? false}
                        onSelect={() => onSelect?.(sug)}
                    />
                ))}
            </View>
        </View>
    );
}

function AnswerBasedCard({
    suggestion,
    readOnly,
    pending,
    onSelect
}: {
    suggestion: AnswerBasedSuggestion;
    readOnly: boolean;
    pending: boolean;
    onSelect: () => void;
}) {
    const tint = CONFIDENCE_TINT[suggestion.confidence];
    return (
        <View style={[dxStyles.card, { borderLeftColor: tint.fg }]}>
            <View style={dxStyles.cardTopRow}>
                <View style={dxStyles.codeBadge}>
                    <Text style={dxStyles.codeBadgeText}>{suggestion.icdCode}</Text>
                </View>
                <View style={[dxStyles.statusBadge, { backgroundColor: tint.bg }]}>
                    <Text style={[dxStyles.statusBadgeText, { color: tint.fg }]}>
                        {suggestion.confidence.toUpperCase()} CONFIDENCE
                    </Text>
                </View>
            </View>
            <Text style={dxStyles.cardDesc} numberOfLines={2}>
                {suggestion.icdText}
            </Text>
            <View style={dxStyles.cardBottomRow}>
                <View style={[dxStyles.sourceChip, { backgroundColor: tint.bg }]}>
                    <Text style={[dxStyles.sourceChipText, { color: tint.fg }]} numberOfLines={1}>
                        {suggestion.matchType}
                    </Text>
                </View>
                <Pressable
                    onPress={onSelect}
                    disabled={readOnly || pending}
                    hitSlop={6}
                    accessibilityRole="button"
                    accessibilityLabel={`Add ${suggestion.icdCode} from your answers`}
                    style={({ pressed }) => [pressed && { opacity: 0.85 }]}
                >
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={dxStyles.selectPill}
                    >
                        {pending ? (
                            <ActivityIndicator size="small" color="#fff" />
                        ) : (
                            <Text style={dxStyles.selectPillText}>ADD</Text>
                        )}
                    </LinearGradient>
                </Pressable>
            </View>
        </View>
    );
}

// ── Phase: idle ────────────────────────────────────────────────────────────

function ReadyDotWithHalo() {
    const pulse = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        const loop = Animated.loop(
            Animated.timing(pulse, {
                toValue: 1,
                duration: 1600,
                easing: Easing.out(Easing.ease),
                useNativeDriver: Platform.OS !== "web"
            })
        );
        loop.start();
        return () => loop.stop();
    }, [pulse]);
    return (
        <View style={dxStyles.readyDotWrap}>
            <Animated.View
                pointerEvents="none"
                style={[
                    dxStyles.readyHalo,
                    {
                        opacity: pulse.interpolate({
                            inputRange: [0, 1],
                            outputRange: [0.6, 0]
                        }),
                        transform: [
                            {
                                scale: pulse.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: [0.9, 1.7]
                                })
                            }
                        ]
                    }
                ]}
            />
            <View style={dxStyles.readyDot} />
        </View>
    );
}

function DxIdle({ onRun, readOnly }: { onRun: () => void; readOnly: boolean }) {
    return (
        <View style={dxStyles.idleColumn}>
            <View style={dxStyles.readyRow}>
                <ReadyDotWithHalo />
                <Text style={dxStyles.readyLabel}>Ready</Text>
            </View>
            <Text style={dxStyles.idleTitle}>Scan answers,{"\n"}narrative &amp; profile.</Text>
            <Text style={dxStyles.idleHelper}>
                AI matches ICD-10 codes from everything entered so far in this visit.
            </Text>
            <View style={dxStyles.chipRow}>
                <SourceChip label="Chart" />
                <SourceChip label="Answers" />
                <SourceChip label="Narrative" />
            </View>
            <Pressable
                onPress={onRun}
                disabled={readOnly}
                accessibilityRole="button"
                accessibilityLabel="Suggest Diagnosis"
                style={({ pressed }) => [dxStyles.runPress, pressed && { opacity: 0.85 }]}
            >
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={dxStyles.runButton}
                >
                    <MaterialCommunityIcons name="creation" size={17} color="#fff" />
                    <Text style={dxStyles.runText}>Suggest Diagnosis</Text>
                    <MaterialCommunityIcons name="arrow-right" size={17} color="#fff" />
                </LinearGradient>
            </Pressable>
            <Text style={dxStyles.idleFootnote}>≈ 10 seconds · reviewed before saving</Text>
        </View>
    );
}

// ── Phase: analyzing ───────────────────────────────────────────────────────

function DxAnalyzing() {
    return (
        <View style={dxStyles.analyzingCol}>
            <ActivityIndicator color={colors.teal600} />
            <Text style={dxStyles.analyzingLabel}>Analyzing for diagnoses…</Text>
        </View>
    );
}

// ── Phase: errored ─────────────────────────────────────────────────────────
//
// Visible when /ai/icd throws (network drop, LLM timeout, 5xx). Gives the
// RN an explicit Retry pill instead of silently dumping her back to idle
// with only a snackbar. Manual-add stays reachable via the AddMorePanel
// rendered below the band in all phases except `analyzing`.

function DxError({ onRetry, readOnly }: { onRetry: () => void; readOnly: boolean }) {
    return (
        <View style={dxStyles.errorCol}>
            <View style={dxStyles.errorRow}>
                <View style={dxStyles.errorIcon}>
                    <MaterialCommunityIcons name="alert" size={16} color="#fff" />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={dxStyles.errorEyebrow}>Scan failed</Text>
                    <Text style={dxStyles.errorTitle}>Couldn&rsquo;t reach AI</Text>
                </View>
            </View>
            <Text style={dxStyles.errorHelper}>
                Your answers are saved. Try again, or add diagnoses manually below.
            </Text>
            <Pressable
                onPress={onRetry}
                disabled={readOnly}
                accessibilityRole="button"
                accessibilityLabel="Retry AI scan"
                style={({ pressed }) => [pressed && { opacity: 0.85 }]}
            >
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={dxStyles.errorRetryBtn}
                >
                    <MaterialCommunityIcons name="refresh" size={15} color="#fff" />
                    <Text style={dxStyles.errorRetryText}>Retry scan</Text>
                </LinearGradient>
            </Pressable>
        </View>
    );
}

// ── Phase: results ─────────────────────────────────────────────────────────

/**
 * Z1 results — list of PENDING AI suggestions only. Selected codes have moved
 * out of this list into Zone 2 (rendered below the band by the parent).
 *
 * Per the canonical design: no Skip button. Absence of Select = rejection.
 * Tapping Complete or editing any answer/narrative auto-deletes everything
 * still in this list (the orchestrator handles that side).
 */
function DxResults({
    pending,
    pendingCodes,
    sectionName,
    onSelect,
    readOnly
}: {
    pending: DiagnosisRow[];
    pendingCodes?: Set<string>;
    sectionName: string;
    onSelect: (code: string) => void;
    readOnly: boolean;
}) {
    const pendingCount = pending.length;
    return (
        <View style={{ gap: 14 }}>
            <View style={dxStyles.resultsChipRow}>
                <View style={dxStyles.resultsChip}>
                    <MaterialCommunityIcons name="creation" size={11} color={colors.teal600} />
                    <Text style={dxStyles.resultsChipText}>
                        {pendingCount === 0
                            ? "ALL SUGGESTIONS REVIEWED"
                            : `${pendingCount} AI ${pendingCount === 1 ? "SUGGESTION" : "SUGGESTIONS"}`}
                    </Text>
                </View>
            </View>
            <Text style={dxStyles.resultsTitle}>Review &amp; select.</Text>

            <View style={{ gap: 8 }}>
                {pendingCount === 0 ? (
                    <Text style={dxStyles.empty}>
                        Every suggestion has been moved to your selected list below.
                    </Text>
                ) : null}
                {pending.map((d) => (
                    <ICDCard
                        key={d.code}
                        d={d}
                        variant="pending"
                        sectionName={sectionName}
                        readOnly={readOnly}
                        pending={pendingCodes?.has(d.code) ?? false}
                        onPrimaryAction={() => onSelect(d.code)}
                    />
                ))}
            </View>
        </View>
    );
}

// ── Zone 2 — Selected for this section (lives BELOW the band) ──────────────

/**
 * Always-rendered card list of codes the RN has saved to this section
 * (AI rows where `confirmed` is true, plus all RN-typed rows).
 * Empty state shows placeholder copy. Each row is the same ICDCard shape
 * as a pending card, but with the "Selected" pill button.
 */
function Zone2List({
    selected,
    pendingCodes,
    sectionName,
    readOnly,
    onUnselectAi,
    onRemoveRn
}: {
    selected: DiagnosisRow[];
    pendingCodes?: Set<string>;
    sectionName: string;
    readOnly: boolean;
    onUnselectAi: (code: string) => void;
    onRemoveRn: (code: string) => void;
}) {
    return (
        <View style={dxStyles.zone2Wrap}>
            <View style={dxStyles.zone2Header}>
                <Text style={dxStyles.zone2Label}>Selected for this section</Text>
                {selected.length > 0 ? (
                    <View style={dxStyles.zone2Count}>
                        <Text style={dxStyles.zone2CountText}>{selected.length}</Text>
                    </View>
                ) : null}
            </View>
            {selected.length === 0 ? (
                <View style={dxStyles.zone2Empty}>
                    <Text style={dxStyles.zone2EmptyText}>
                        Nothing selected yet.
                        {readOnly ? "" : " Tap Select on a suggestion above, or add manually."}
                    </Text>
                </View>
            ) : (
                <View style={{ gap: 8 }}>
                    {selected.map((d) => (
                        <ICDCard
                            key={d.code}
                            d={d}
                            variant="selected"
                            sectionName={sectionName}
                            readOnly={readOnly}
                            pending={pendingCodes?.has(d.code) ?? false}
                            onPrimaryAction={() => {
                                if (d.suggestedByAi) {
                                    onUnselectAi(d.code);
                                } else {
                                    onRemoveRn(d.code);
                                }
                            }}
                        />
                    ))}
                </View>
            )}
        </View>
    );
}

// ── OtherStripe — collapsible stripe for codes not relevant to this section ─

function OtherStripe({
    rows,
    sectionName,
    readOnly
}: {
    rows: DiagnosisRow[];
    sectionName: string;
    readOnly: boolean;
}) {
    const [open, setOpen] = useState(false);
    if (rows.length === 0) {
        return null;
    }
    return (
        <View style={dxStyles.otherWrap}>
            <Pressable
                onPress={() => setOpen((v) => !v)}
                accessibilityRole="button"
                accessibilityLabel={
                    open
                        ? "Hide other patient diagnoses"
                        : `Show ${rows.length} other patient diagnoses`
                }
                style={dxStyles.otherToggle}
            >
                <Text style={dxStyles.otherToggleText}>
                    {open
                        ? "Hide other patient diagnoses"
                        : `Show other patient diagnoses (${rows.length})`}
                </Text>
                <MaterialCommunityIcons
                    name={open ? "chevron-up" : "chevron-down"}
                    size={16}
                    color={colors.textSecondary}
                />
            </Pressable>
            {open ? (
                <View style={dxStyles.otherList}>
                    {rows.map((d) => (
                        <ICDCard
                            key={d.code}
                            d={d}
                            variant="locked"
                            sectionName={sectionName}
                            readOnly={readOnly}
                        />
                    ))}
                </View>
            ) : null}
        </View>
    );
}

// ── ICDCard (variant-driven, three rendering modes) ────────────────────────
//
//   variant = "pending"  — Z1 inside the band: brand-gradient SELECT pill
//                          (tap → onPrimaryAction sets confirmed=true)
//   variant = "selected" — Z2 below the band: teal SELECTED pill
//                          (tap → onPrimaryAction unselects/removes the row)
//   variant = "locked"   — Z3 inside the OtherStripe: no action button
//
// Skip / round-checkbox patterns from v1 are gone — absence of Select = reject.

type ICDCardVariant = "pending" | "selected" | "locked";

function ICDCard({
    d,
    variant,
    sectionName,
    readOnly,
    pending = false,
    onPrimaryAction
}: {
    d: DiagnosisRow;
    variant: ICDCardVariant;
    sectionName: string;
    readOnly: boolean;
    pending?: boolean;
    onPrimaryAction?: () => void;
}) {
    const isRn = !d.suggestedByAi && !d.alreadyOnProfile;
    const locked = variant === "locked";

    // Card chrome — left-edge accent + status badge tinting.
    const accent = locked ? colors.gray300 : isRn ? colors.pink400 : colors.teal400;
    const badgeBg = locked ? colors.gray100 : isRn ? colors.pink50 : colors.teal50;
    const badgeFg = locked ? colors.gray500 : isRn ? colors.pink500 : colors.teal600;
    const badgeLabel = locked ? "On profile" : isRn ? "Added by you" : "AI suggested";

    return (
        <View style={[dxStyles.card, { borderLeftColor: accent }]}>
            <View style={dxStyles.cardTopRow}>
                <View style={dxStyles.codeBadge}>
                    <Text style={dxStyles.codeBadgeText}>{d.code}</Text>
                </View>
                <View style={[dxStyles.statusBadge, { backgroundColor: badgeBg }]}>
                    <Text style={[dxStyles.statusBadgeText, { color: badgeFg }]}>{badgeLabel}</Text>
                </View>
            </View>
            <Text style={dxStyles.cardDesc} numberOfLines={2}>
                {d.description}
            </Text>
            <View style={dxStyles.cardBottomRow}>
                <View style={[dxStyles.sourceChip, { backgroundColor: badgeBg }]}>
                    {isRn ? <MaterialCommunityIcons name="plus" size={11} color={badgeFg} /> : null}
                    {locked ? (
                        <MaterialCommunityIcons name="diamond-outline" size={11} color={badgeFg} />
                    ) : null}
                    <Text style={[dxStyles.sourceChipText, { color: badgeFg }]}>
                        {locked ? "Pre-visit profile" : isRn ? "Added manually" : sectionName}
                    </Text>
                </View>
                {variant === "pending" ? (
                    <Pressable
                        onPress={onPrimaryAction}
                        disabled={readOnly || pending}
                        hitSlop={6}
                        accessibilityRole="button"
                        accessibilityLabel={`Select ${d.code}`}
                        style={({ pressed }) => [pressed && { opacity: 0.85 }]}
                    >
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={dxStyles.selectPill}
                        >
                            {pending ? (
                                <ActivityIndicator size="small" color="#fff" />
                            ) : (
                                <Text style={dxStyles.selectPillText}>SELECT</Text>
                            )}
                        </LinearGradient>
                    </Pressable>
                ) : null}
                {variant === "selected" ? (
                    <Pressable
                        onPress={onPrimaryAction}
                        disabled={readOnly || pending}
                        hitSlop={6}
                        accessibilityRole="button"
                        accessibilityLabel={`Remove ${d.code} from this section`}
                        style={({ pressed }) => [
                            dxStyles.selectedPill,
                            pressed && { opacity: 0.85 }
                        ]}
                    >
                        {pending ? (
                            <ActivityIndicator size="small" color={colors.teal600} />
                        ) : (
                            <>
                                <MaterialCommunityIcons
                                    name="check"
                                    size={12}
                                    color={colors.teal600}
                                />
                                <Text style={dxStyles.selectedPillText}>SELECTED</Text>
                            </>
                        )}
                    </Pressable>
                ) : null}
                {/* locked variant: no action button */}
            </View>
        </View>
    );
}

// ── Add More — inline pink-bordered search panel (no modal) ────────────────

function AddMorePanel({
    existingCodes,
    onAdd,
    readOnly,
    glow
}: {
    existingCodes: Set<string>;
    onAdd: (code: string, description: string) => void;
    readOnly: boolean;
    glow: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const inputRef = useRef<TextInput>(null);

    // Common codes first (instant), then the full NLM ICD-10-CM library appended.
    const { results, loading } = useIcdSearch(open ? query : "", existingCodes);

    const handleAdd = (code: string, description: string) => {
        onAdd(code, description);
        setQuery("");
        // Re-focus to keep the iOS keyboard up across consecutive adds.
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    if (readOnly) {
        return null;
    }

    if (!open) {
        return (
            <Pressable
                onPress={() => setOpen(true)}
                accessibilityRole="button"
                accessibilityLabel="Search diagnosis manually"
                style={[
                    dxStyles.addTrigger,
                    glow ? dxStyles.addTriggerGlow : dxStyles.addTriggerDefault
                ]}
            >
                <MaterialCommunityIcons name="magnify" size={15} color={colors.teal600} />
                <Text style={[dxStyles.addTriggerText, { color: colors.teal600 }]}>
                    Search diagnosis manually
                </Text>
            </Pressable>
        );
    }

    return (
        <View style={dxStyles.searchPanel}>
            <View style={dxStyles.searchHeader}>
                <Text style={dxStyles.searchTitle}>ADD DIAGNOSIS</Text>
                <Pressable
                    onPress={() => {
                        setOpen(false);
                        setQuery("");
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Cancel"
                >
                    <Text style={dxStyles.searchCancel}>Cancel</Text>
                </Pressable>
            </View>
            <View style={dxStyles.searchInputWrap}>
                <MaterialCommunityIcons
                    name="magnify"
                    size={16}
                    color={colors.gray500}
                    style={dxStyles.searchIcon}
                />
                <TextInput
                    ref={inputRef}
                    value={query}
                    onChangeText={setQuery}
                    placeholder="Search by code or description…"
                    placeholderTextColor={colors.gray400}
                    autoCapitalize="characters"
                    autoCorrect={false}
                    autoFocus
                    style={dxStyles.searchInput}
                />
            </View>
            {results.length > 0 ? (
                <View style={dxStyles.searchResults}>
                    {results.map((r, i) => (
                        <Pressable
                            key={r.code}
                            onPress={() => handleAdd(r.code, r.description)}
                            accessibilityRole="button"
                            accessibilityLabel={`Add ${r.code} ${r.description}`}
                            style={[
                                dxStyles.searchRow,
                                i === results.length - 1 ? null : dxStyles.searchRowDivider
                            ]}
                        >
                            <Text style={dxStyles.searchRowCode}>{r.code}</Text>
                            <Text style={dxStyles.searchRowDesc} numberOfLines={1}>
                                {r.description}
                            </Text>
                        </Pressable>
                    ))}
                </View>
            ) : null}
            {loading ? (
                <View style={dxStyles.searchStatusRow}>
                    <ActivityIndicator size="small" color={colors.teal600} />
                    <Text style={dxStyles.searchEmpty}>Searching full ICD library…</Text>
                </View>
            ) : null}
            {!loading && query.trim().length >= 2 && results.length === 0 ? (
                <Text style={dxStyles.searchEmpty}>No matching diagnosis codes</Text>
            ) : null}
        </View>
    );
}

// ── Merge helper used by the orchestrator ──────────────────────────────────

/**
 * Build the four-kind row list. Includes ALL patient diagnoses (profile +
 * every section's current_visit codes) so the "Other" stripe can render
 * irrelevant codes folded — the user-facing rule:
 *
 *   MAIN list (always visible)
 *     - AI-suggested codes (this section)
 *     - RN-added codes (this section)
 *     - AI-suggested codes that happen to already be on profile —
 *       rendered as locked cards so the RN can see "AI flagged this,
 *       you already have it"
 *
 *   OTHER stripe (collapsible)
 *     - Patient-profile codes the AI did NOT surface this run
 *     - Cross-section current_visit codes (added in another section)
 *
 * `alreadyOnProfile` is the lock flag: true means the card can't toggle /
 * remove (the code is owned elsewhere). The orchestrator uses it to keep
 * locked rows out of the saveDiagnoses payload.
 */
export function buildDiagnosisRows(
    ai: ICDSuggestion[],
    patientDiagnoses: PatientDiagnosisItem[],
    sectionId: string | undefined
): DiagnosisRow[] {
    const sectionIdNum = sectionId != null ? Number(sectionId) : null;
    const profileByCode = new Map(patientDiagnoses.map((d) => [d.icdCode, d]));
    const out: DiagnosisRow[] = [];
    const seen = new Set<string>();
    for (const s of ai) {
        const existing = profileByCode.get(s.code);
        const isOnProfile =
            existing != null &&
            (existing.source === "profile" ||
                (existing.source === "current_visit" &&
                    sectionIdNum != null &&
                    existing.sectionId !== sectionIdNum));
        // If this AI suggestion is ALREADY saved to this same section, the
        // RN previously tapped Select on it. Carry forward confirmed=true
        // and the visit_diagnoses.id so it renders in Zone 2 (Selected),
        // not Zone 1 (pending). Without this guard, every re-run of the
        // scan demoted Selected rows back to pending — destructive UX.
        const alreadySelectedInThisSection =
            existing != null &&
            existing.source === "current_visit" &&
            sectionIdNum != null &&
            existing.sectionId === sectionIdNum;
        // Fresh AI suggestions land in Zone 1 pending — the RN MUST tap
        // SELECT to attach to this section. Profile-overlapping codes go
        // to Zone 3 (locked) via alreadyOnProfile=true.
        // (The OLD pre-confirmed-checkbox behavior auto-selected here;
        // the new 3-zone canonical model is opt-in only.)
        out.push({
            code: s.code,
            description: s.description,
            confirmed: alreadySelectedInThisSection,
            suggestedByAi: true,
            alreadyOnProfile: isOnProfile,
            id: alreadySelectedInThisSection ? existing.id : undefined
        });
        seen.add(s.code);
    }
    for (const d of patientDiagnoses) {
        if (seen.has(d.icdCode)) {
            continue;
        }
        const inThisSection =
            d.source === "current_visit" && sectionIdNum != null && d.sectionId === sectionIdNum;
        out.push({
            code: d.icdCode,
            description: d.description,
            confirmed: inThisSection,
            suggestedByAi: false,
            // Lock anything NOT confirmed in this section — profile codes
            // and cross-section current_visit codes both behave the same
            // here: read-only, no save.
            alreadyOnProfile: !inThisSection,
            id: d.id
        });
        seen.add(d.icdCode);
    }
    return out;
}

/**
 * Splits rows into MAIN (AI + RN-added in this section) vs OTHER (locked
 * profile + cross-section). Used by DiagnosesChapter to render the main
 * list above the collapsible "Show other" stripe.
 */
export function splitDiagnosisRows(rows: DiagnosisRow[]): {
    main: DiagnosisRow[];
    other: DiagnosisRow[];
} {
    const main: DiagnosisRow[] = [];
    const other: DiagnosisRow[] = [];
    for (const r of rows) {
        // AI hits always belong to main (even if locked-already-on-profile),
        // so the RN sees the LLM's signal. RN-added codes in this section
        // are main. Everything else (profile codes the AI didn't surface,
        // cross-section codes) is "other".
        if (r.suggestedByAi || (!r.suggestedByAi && !r.alreadyOnProfile)) {
            main.push(r);
        } else {
            other.push(r);
        }
    }
    return { main, other };
}

/**
 * 3-zone split per the canonical chapter-03 design:
 *
 *   pending  = Z1 (inside the band)       — AI suggested, not yet selected, not locked
 *   selected = Z2 (below the band)        — anything saved-to-this-section (AI+RN)
 *   other    = Z3 (collapsed strip below) — locked profile + cross-section codes
 */
export function splitIntoZones(rows: DiagnosisRow[]): {
    pending: DiagnosisRow[];
    selected: DiagnosisRow[];
    other: DiagnosisRow[];
} {
    const pending: DiagnosisRow[] = [];
    const selected: DiagnosisRow[] = [];
    const other: DiagnosisRow[] = [];
    for (const r of rows) {
        if (r.alreadyOnProfile) {
            other.push(r);
        } else if (r.confirmed) {
            selected.push(r);
        } else if (r.suggestedByAi) {
            pending.push(r);
        }
    }
    return { pending, selected, other };
}

// ── Styles ─────────────────────────────────────────────────────────────────

const dxStyles = StyleSheet.create({
    idleColumn: { gap: 12 },
    readyRow: { flexDirection: "row", alignItems: "center", gap: 8 },
    readyDotWrap: {
        width: 18,
        height: 18,
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible"
    },
    readyDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: colors.teal400
    },
    readyHalo: {
        position: "absolute",
        top: -4,
        left: -4,
        right: -4,
        bottom: -4,
        borderRadius: 13,
        borderWidth: 2,
        borderColor: colors.teal400
    },
    readyLabel: {
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: colors.teal600,
        letterSpacing: 1.2,
        textTransform: "uppercase"
    },
    idleTitle: {
        fontFamily: fonts.extraBold,
        fontSize: 24,
        color: colors.ink,
        letterSpacing: -0.6,
        lineHeight: 28
    },
    idleHelper: {
        fontFamily: fonts.medium,
        fontSize: 12.5,
        color: colors.textSecondary,
        lineHeight: 18,
        maxWidth: 280
    },
    chipRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 4
    },
    runPress: { marginTop: 8 },
    runButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        paddingVertical: 16,
        paddingHorizontal: 20,
        borderRadius: 16,
        ...Platform.select({
            ios: {
                shadowColor: "#45D2B0",
                shadowOffset: { width: 0, height: 16 },
                shadowOpacity: 0.4,
                shadowRadius: 32
            },
            android: { elevation: 8 },
            default: {}
        })
    },
    runText: {
        fontFamily: fonts.extraBold,
        fontSize: 16,
        color: "#fff",
        letterSpacing: -0.2
    },
    idleFootnote: {
        marginTop: 4,
        textAlign: "center",
        fontFamily: fonts.semiBold,
        fontSize: 11,
        color: colors.textTertiary,
        letterSpacing: 0.2
    },
    analyzingCol: {
        gap: 14,
        alignItems: "center",
        paddingVertical: 22
    },
    analyzingLabel: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.textSecondary
    },
    errorCol: { gap: 12 },
    errorRow: { flexDirection: "row", alignItems: "center", gap: 10 },
    errorIcon: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: colors.error500,
        alignItems: "center",
        justifyContent: "center"
    },
    errorEyebrow: {
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: colors.error600,
        letterSpacing: 1,
        textTransform: "uppercase"
    },
    errorTitle: {
        fontFamily: fonts.extraBold,
        fontSize: 18,
        color: colors.ink,
        letterSpacing: -0.3
    },
    errorHelper: {
        fontFamily: fonts.medium,
        fontSize: 12.5,
        color: colors.textSecondary,
        lineHeight: 18
    },
    errorRetryBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 18,
        borderRadius: 999,
        alignSelf: "flex-start"
    },
    errorRetryText: {
        fontFamily: fonts.extraBold,
        fontSize: 13,
        color: "#fff",
        letterSpacing: -0.1
    },
    resultsChipRow: { flexDirection: "row", alignItems: "center", gap: 10 },
    resultsChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 5,
        paddingHorizontal: 12,
        borderRadius: 999,
        backgroundColor: "rgba(69,210,176,0.18)"
    },
    resultsChipText: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: colors.teal600,
        letterSpacing: 0.5
    },
    resultsTitle: {
        fontFamily: fonts.extraBold,
        fontSize: 22,
        color: colors.ink,
        letterSpacing: -0.5
    },
    abGroup: { gap: 10, marginBottom: 16 },
    abHelper: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textSecondary,
        lineHeight: 18
    },
    empty: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textTertiary,
        fontStyle: "italic",
        textAlign: "center",
        paddingVertical: 24
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        borderLeftWidth: 4,
        padding: 14,
        paddingLeft: 16,
        gap: 8
    },
    cardTopRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    codeBadge: {
        backgroundColor: colors.teal50,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4
    },
    codeBadgeText: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.teal600,
        letterSpacing: 0.3
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4
    },
    statusBadgeText: {
        fontFamily: fonts.bold,
        fontSize: 10,
        letterSpacing: 0.6,
        textTransform: "uppercase"
    },
    cardDesc: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.ink,
        lineHeight: 20
    },
    cardBottomRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    sourceChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 20
    },
    sourceChipText: {
        fontFamily: fonts.semiBold,
        fontSize: 11
    },
    // ── Select / Selected pill buttons (per the canonical Chapter-03 spec) ──
    selectPill: {
        paddingVertical: 7,
        paddingHorizontal: 14,
        borderRadius: 999,
        ...Platform.select({
            ios: {
                shadowColor: colors.teal400,
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.25,
                shadowRadius: 8
            },
            android: { elevation: 2 },
            default: {}
        })
    },
    selectPillText: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: "#fff",
        letterSpacing: 0.6
    },
    selectedPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderRadius: 999,
        backgroundColor: colors.teal50,
        borderWidth: 1.5,
        borderColor: colors.teal400
    },
    selectedPillText: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: colors.teal600,
        letterSpacing: 0.6
    },
    // ── Zone 2 — Selected for this section (lives below the band) ──
    zone2Wrap: {
        marginTop: 18
    },
    zone2Header: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 10
    },
    zone2Label: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: colors.textSecondary,
        letterSpacing: 0.8,
        textTransform: "uppercase"
    },
    zone2Count: {
        backgroundColor: colors.ink,
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: 999
    },
    zone2CountText: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: "#fff",
        letterSpacing: 0.3
    },
    zone2Empty: {
        backgroundColor: colors.gray100,
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: colors.gray300,
        borderRadius: 14,
        padding: 16
    },
    zone2EmptyText: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textTertiary,
        textAlign: "center",
        lineHeight: 18
    },
    addTrigger: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderRadius: 12
    },
    addTriggerDefault: {
        borderWidth: 1.5,
        borderColor: colors.teal400,
        backgroundColor: colors.surface,
        ...Platform.select({
            ios: {
                shadowColor: colors.teal400,
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.18,
                shadowRadius: 10
            },
            android: { elevation: 2 },
            default: {}
        })
    },
    addTriggerGlow: {
        borderWidth: 1.5,
        borderColor: colors.teal400,
        backgroundColor: "rgba(69,210,176,0.10)",
        ...Platform.select({
            ios: {
                shadowColor: colors.teal400,
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0.35,
                shadowRadius: 14
            },
            android: { elevation: 4 },
            default: {}
        })
    },
    addTriggerText: {
        fontFamily: fonts.extraBold,
        fontSize: 13.5,
        letterSpacing: -0.1
    },
    searchPanel: {
        padding: 12,
        backgroundColor: colors.surface,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: colors.pink400,
        gap: 10
    },
    searchHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    searchTitle: {
        fontFamily: fonts.bold,
        fontSize: 11,
        color: colors.pink500,
        letterSpacing: 0.5
    },
    searchCancel: {
        fontFamily: fonts.semiBold,
        fontSize: 12,
        color: colors.teal600
    },
    searchInputWrap: {
        position: "relative"
    },
    searchIcon: {
        position: "absolute",
        left: 10,
        top: 12,
        zIndex: 1
    },
    searchInput: {
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: 10,
        paddingVertical: 10,
        paddingLeft: 32,
        paddingRight: 12,
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.ink,
        backgroundColor: colors.surface
    },
    searchEmpty: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.gray500,
        fontStyle: "italic",
        textAlign: "center",
        paddingVertical: 8
    },
    searchStatusRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 4
    },
    searchResults: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 10,
        backgroundColor: colors.surface,
        overflow: "hidden"
    },
    searchRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingVertical: 11,
        paddingHorizontal: 12
    },
    searchRowDivider: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    searchRowCode: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.teal600,
        minWidth: 56
    },
    searchRowDesc: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.gray600,
        flex: 1
    },
    otherWrap: {
        gap: 8,
        marginTop: 18
    },
    otherToggle: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 14,
        borderRadius: 999,
        backgroundColor: "rgba(11,11,31,0.04)",
        borderWidth: 1,
        borderColor: "rgba(11,11,31,0.08)"
    },
    otherToggleText: {
        fontFamily: fonts.bold,
        fontSize: 12,
        color: colors.textSecondary,
        letterSpacing: 0.3,
        textTransform: "uppercase"
    },
    otherList: {
        gap: 8
    }
});
